import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const STATUS_OPTIONS = ['new', 'contacted', 'answered', 'closed'];

const statusClass = (status) => {
  const map = {
    new: 'status-new',
    contacted: 'status-contacted',
    answered: 'status-answered',
    closed: 'status-closed',
  };
  return map[status] || 'status-pending';
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-PK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const label = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const vehicleName = (row) => {
  const name = row.vehicle_name || '';
  const variant = row.vehicle_variant || '';
  if (variant && !name.toLowerCase().includes(variant.toLowerCase())) return `${name} ${variant}`.trim();
  return name;
};

/**
 * Full-page inquiry view (/dashboard/inquiries/:inquiryId), like ApplicationDetail: the admin reads the
 * whole request and sets its status here instead of through a dropdown squeezed into the table.
 */
const InquiryDetail = ({ inquiries, isAdmin, loading, onStatusChange, onDelete }) => {
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const row = inquiries.find((item) => String(item.id) === String(inquiryId));
  const back = () => navigate('/dashboard/inquiries');

  if (!row) {
    return (
      <section className="dash-panel application-detail">
        <p>{loading ? 'Loading inquiry…' : 'This inquiry is not in the current list.'}</p>
        <button type="button" className="dash-btn-reset-filters" onClick={back}>
          <i className="fas fa-arrow-left" /> Back to inquiries
        </button>
      </section>
    );
  }

  const status = (row.status || 'new').toLowerCase();
  const isClosed = status === 'closed';

  return (
    <section className="dash-panel application-detail inquiry-detail">
      <div className="application-detail-head">
        <button type="button" className="dash-btn-reset-filters" onClick={back}>
          <i className="fas fa-arrow-left" /> Inquiries
        </button>
        <div>
          <h2>Inquiry #{row.id}</h2>
          <p className="dash-muted">{row.customer_name}</p>
        </div>
        <div className="application-detail-actions">
          <span className={`dash-status-pill ${statusClass(status)}`}>
            {isClosed ? <i className="fas fa-lock" /> : null} {status}
          </span>
        </div>
      </div>

      <div className="application-detail-grid">
        <div className="detail-card">
          <h3>Customer</h3>
          <p>{row.customer_name}</p>
          <p>{row.phone}</p>
          {row.email ? <p>{row.email}</p> : null}
        </div>
        <div className="detail-card">
          <h3>Vehicle</h3>
          <p>{vehicleName(row) || 'No vehicle'}</p>
        </div>
        <div className="detail-card">
          <h3>Request</h3>
          <p>{label(row.inquiry_type) || 'Inquiry'}</p>
          <p className="dash-muted">Source: {label(row.source || 'site')}</p>
          <p className="dash-muted">Submitted {formatDate(row.created_at)}</p>
        </div>
      </div>

      <div className="application-detail-notes">
        <h3>Message</h3>
        <p className="dash-notes">{row.message || 'No message was written.'}</p>
      </div>

      {isAdmin ? (
        <div className="application-detail-controls">
          <h3>Status</h3>
          {isClosed ? (
            <p className="dash-muted">This inquiry is closed and can no longer be changed.</p>
          ) : (
            <div className="status-actions">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={status === option ? 'active' : ''}
                  disabled={status === option}
                  onClick={() => onStatusChange(row.id, option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
          {onDelete ? (
            <button type="button" className="dash-delete-btn" onClick={() => onDelete(row)}>
              <i className="fas fa-trash" /> Delete inquiry
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default InquiryDetail;
